import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  useArchiveExternalIntegrationConfigMutation,
  useCreateExternalIntegrationConfigMutation,
  useDeleteExternalIntegrationConfigMutation,
  useListExternalIntegrationConfigsQuery,
} from 'dashboard/redux/api/external-integration-config';
import { useGetExternalIntegrationVendorQuery } from 'dashboard/redux/api/external-integration-vendor';
import DashboardIntegrationsShowView from 'dashboard/views/DashboardIntegrationsShowView';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';

type Params = { vendorKey: string };

const DashboardIntegrationsShowRoute = () => {
  const { vendorKey } = useParams<Params>() as Params; // vendorKey is always defined

  const {
    data: externalIntegrationVendor,
    isLoading,
    error: getVendorError,
  } = useGetExternalIntegrationVendorQuery(vendorKey);

  useRTKErrorHandler({
    error: getVendorError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const { data: externalIntegrationConfigs, isFetching: isFetchingConfigs } =
    useListExternalIntegrationConfigsQuery();

  const vendorConfigs = useMemo(
    () =>
      (externalIntegrationConfigs || [])
        .filter((config) => config.attributes['vendor-name'] === externalIntegrationVendor?.vendor)
        .sort(
          (first, second) =>
            DateTime.fromISO(first.attributes['created-at']).toUnixInteger() -
            DateTime.fromISO(second.attributes['created-at']).toUnixInteger(),
        ),
    [externalIntegrationConfigs, externalIntegrationVendor],
  );

  const [createConfig, { error: createConfigError, isLoading: isCreatingConfig }] =
    useCreateExternalIntegrationConfigMutation();

  useRTKErrorHandler({
    error: createConfigError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const [onDeleteConfig, { error: deleteConfigError }] =
    useDeleteExternalIntegrationConfigMutation();
  useRTKErrorHandler({
    error: deleteConfigError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const [onArchiveConfig, { error: archiveConfigError }] =
    useArchiveExternalIntegrationConfigMutation();
  useRTKErrorHandler({
    error: archiveConfigError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  if (!isLoading && externalIntegrationVendor) {
    return (
      <DashboardIntegrationsShowView
        vendor={externalIntegrationVendor}
        onCreateConfig={createConfig}
        onDeleteConfig={onDeleteConfig}
        onArchiveConfig={onArchiveConfig}
        vendorConfigs={vendorConfigs}
        isCreatingConfig={isCreatingConfig}
        isFetchingConfigs={isFetchingConfigs}
      />
    );
  } else {
    return <LoadingIndicator fullscreen />;
  }
};

export default DashboardIntegrationsShowRoute;
